import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { apiClient } from '../common/ApiClient';
import { createStore } from '../common/Store';
import { useUserInfoHeaders } from './useUserInfoHeaders';

const CSRF_TOKEN_HEADER_NAME = 'x-csrf-token';

export const CsrfStore = createStore<{ csrfToken: string }>();

export const CsrfTokenProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const { [CSRF_TOKEN_HEADER_NAME]: initialCsrfToken } = useUserInfoHeaders();
  const csrfToken = useRef<string>(initialCsrfToken);
  const [ready, setReady] = useState(Boolean(initialCsrfToken));

  // Set ready to true once the initial CSRF token is loaded to prevent
  // any requests without CSRF token being sent by child components.
  useEffect(() => {
    if (initialCsrfToken) {
      setReady(true);
    }
  }, [initialCsrfToken]);

  useEffect(() => {
    const subscriptions = [
      // Renew CSRF token from response headers of any request
      apiClient.subscribe({
        event: 'response',
        onSuccess: (response) => {
          const csrfTokenFromHeader = response.headers[CSRF_TOKEN_HEADER_NAME];
          if (csrfTokenFromHeader) {
            csrfToken.current = csrfTokenFromHeader;
          }
          return response;
        },
      }),
      // Add CSRF token to headers of all requests
      apiClient.subscribe({
        event: 'request',
        onSuccess: (config) => ({
          ...config,
          headers: {
            ...config.headers,
            [CSRF_TOKEN_HEADER_NAME]: csrfToken.current,
          },
        }),
      }),
    ];
    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <CsrfStore.Provider initialData={{ csrfToken: csrfToken.current }}>
      {children}
    </CsrfStore.Provider>
  );
};
